import React, { useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { imgurl } from "../baseurl";

function HeroSlide({ banners = [], loading = false }) {
  const [current, setCurrent] = useState(0);
  const length = banners.length;

  useEffect(() => {
    if (loading || length === 0) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % length);
    }, 5000); // 5 seconds is better for e-commerce
    return () => clearInterval(timer);
  }, [length, loading]);

  if (loading) {
    return (
      <div className="hero-container mb-4">
        <Skeleton height="100%" containerClassName="d-block aspect-ratio-skeleton" />
      </div>
    );
  }

  if (length === 0) {
    return <div className="text-center py-5 text-muted border rounded">No promotions currently available</div>;
  }

  const nextSlide = () => setCurrent((current + 1) % length);
  const prevSlide = () => setCurrent((current - 1 + length) % length);

  return (
    <div className="hero-container mb-4">
      <div className="hero-slider shadow-sm">
        {/* Slides Wrapper */}
        <div
          className="slides-container"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {banners.map((b, idx) => (
            <div className="slide" key={idx}>
              <a href={b.link || "#"} className="d-block h-100 overflow-hidden">
                <img
                  src={`${imgurl}${b.image_path}`}
                  alt={b.title || "Banner"}
                  className={`banner-img ${current === idx ? "zoom-effect" : ""}`}
                />
              </a>
            </div>
          ))}
        </div>

        {/* Premium Navigation Arrows */}
        {length > 1 && (
          <>
            <button className="nav-arrow left" onClick={prevSlide} aria-label="Previous">
              <FiChevronLeft size={24} />
            </button>
            <button className="nav-arrow right" onClick={nextSlide} aria-label="Next">
              <FiChevronRight size={24} />
            </button>
          </>
        )}

        {/* Modern Progress Dashes */}
        <div className="dash-indicators">
          {banners.map((_, idx) => (
            <div
              key={idx}
              className={`dash ${current === idx ? "active" : ""}`}
              onClick={() => setCurrent(idx)}
            >
              <div className="dash-progress"></div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .hero-container {
            width: 100%;
            max-width: 1400px;
            margin: 0 auto;
        }

        .hero-slider {
            position: relative;
            overflow: hidden;
            border-radius: 12px; /* Smoother rounding */
            background: #f8f9fa;
            aspect-ratio: 16 / 6; /* Responsive ratio: Width 16, Height 6 */
        }

        @media (max-width: 768px) {
            .hero-slider { aspect-ratio: 16 / 7; } /* Taller ratio for mobile */
        }

        .slides-container {
            display: flex;
            height: 100%;
            transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide {
            minWidth: 100%;
            flex: 0 0 100%;
            height: 100%;
        }

        .banner-img {
            width: 100%;
            height: 100%;
            object-fit: cover; /* "cover" is usually better than "fill" for photography */
            transition: transform 6s linear;
        }

        /* Subtle Ken Burns Effect */
        .zoom-effect {
            transform: scale(1.08);
        }

        /* Glassmorphism Arrows */
        .nav-arrow {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.2);
            backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            color: white;
            width: 45px;
            height: 45px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            opacity: 0; /* Hidden by default */
            z-index: 10;
        }

        .hero-slider:hover .nav-arrow {
            opacity: 1;
        }

        .nav-arrow:hover {
            background: rgba(255, 255, 255, 0.9);
            color: #333;
        }

        .nav-arrow.left { left: 20px; }
        .nav-arrow.right { right: 20px; }

        /* Dash Progress Indicators */
        .dash-indicators {
            position: absolute;
            bottom: 50px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 8px;
            z-index: 10;
        }

        .dash {
            width: 30px;
            height: 4px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 2px;
            cursor: pointer;
            overflow: hidden;
            position: relative;
        }

        .dash.active {
            width: 45px; /* Widens the active dash */
            background: rgba(255, 255, 255, 0.4);
            transition: width 0.3s ease;
        }

        .dash-progress {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: 0%;
            background: white;
        }

        .dash.active .dash-progress {
            animation: slideProgress 5s linear forwards;
        }

        @keyframes slideProgress {
            from { width: 0%; }
            to { width: 100%; }
        }

        .aspect-ratio-skeleton {
            aspect-ratio: 16 / 5;
            display: block;
        }
      `}</style>
    </div>
  );
}

export default HeroSlide;